'use client';
import React, { createContext, useContext } from 'react';
import { useTable } from '../../hook';
import { ITableComponentProps } from '../table/table';
import type { Table } from '@tanstack/react-table';

const TableContext = createContext<{
  table: Table<any>;
  selectedRows: any[];
  trophyColumns: Record<string, any>;
  onSearch: (search: string) => void;
}>({
  table: {} as Table<any>,
  trophyColumns: {},
  selectedRows: [],
  onSearch: () => {},
});

export const useTableContext = () => {
  const context = useContext(TableContext);
  if (!context) {
    throw new Error('useTableContext must be used within a TableProvider');
  }
  return context;
};

export const withProvider =
  (Component: React.FC<any>) =>
  (props: ITableComponentProps & ITableProviderProps) => {
    const [search, setSearch] = React.useState<string>('');

    const { table, trophyColumns } = useTable({
      data: props.data,
      columns: props.columns,
      tableConfigName: props.tableConfigName,
      tableState: props.tableState,
      onTableStateChange: props.onTableStateChange,
      disableSort: props.disableSort,
      includeRowSelection: props.includeRowSelection!,
      checkboxSize: props?.checkboxSize,
      search,
      onSearch: setSearch,
    });

    const selectedRows = table
      .getSelectedRowModel()
      .rows.map((row) => row.original);

    return (
      <TableContext.Provider
        value={{
          table,
          selectedRows,
          trophyColumns,
          onSearch: setSearch,
        }}
      >
        <Component {...props} />
      </TableContext.Provider>
    );
  };

export interface ITableProviderProps {
  tableState?: Record<string, any>;
  tableConfigName?: string;
  onTableStateChange?: (state: Record<string, any>) => void;
}
